import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import type { ProfileType } from '../../types/profile';
import { ProfileStepHeader } from './ProfileStepHeader';
import { useAuth } from '../../context/auth';
import { useProfileStore } from '../../stores/profileStore';
import { ROLES } from '../../types/auth';

interface ProfileTypeStepProps {
  onSelect: (type: ProfileType) => void;
}

export default function ProfileTypeStep({ onSelect }: ProfileTypeStepProps) {
  const { t } = useTranslation('auth');
  const { user } = useAuth();
  const { upsertProfileType } = useProfileStore();

  const firstName = user?.user_metadata?.firstName || '';

  const handleProfileType = async (type: ProfileType) => {
    if (!user || !firstName) return;
    try {
      await upsertProfileType(user.id, firstName, type);
      onSelect(type);
    } catch (err) {
      Sentry.captureException(err);
      toast.error(t('errors.generic'));
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:min-h-[calc(100vh-4rem)]md:px-8 lg:px-[9.38rem]">
      <div className="flex flex-col w-full pb-8 md:w-2/3 pt-28 md:pr-8">
        <ProfileStepHeader
          firstName={firstName}
          welcome={t('profileTypeStep.welcome')}
          completeSignup={t('completeSignup')}
          step={t('profileTypeStep.step')}
          completeInfo={t('profileTypeStep.completeInfo')}
          stepDesc={t('profileTypeStep.stepDesc')}
        >
          <div className="font-ambit text-[1.0625rem] not-italic font-[900] leading-[1.5625rem] text-[#3B5266] mt-8">
            {t('common:role.freelancer')}
          </div>
          <div className="font-ambit text-[#3B5266] text-[0.9375rem] not-italic font-normal leading-[1.25rem]">
            {t('profileTypeStep.freelancerDescription')}
          </div>
          <div className="font-ambit text-[1.0625rem] not-italic font-[900] leading-[1.5625rem] text-[#3B5266] mt-8">
            {t('common:role.recruiter')}
          </div>
          <div className="font-ambit text-[#3B5266] text-[0.9375rem] not-italic font-normal leading-[1.25rem] mb-4">
            {t('profileTypeStep.recruiterDescription')}
          </div>
        </ProfileStepHeader>
      </div>
      <div className="flex flex-col items-center justify-center w-full py-16 md:w-1/3">
        <div className="w-full max-w-md">
          <div className="font-ambit text-[1.5625rem] not-italic font-[900] leading-[1.875rem] text-secondary">
            {t('profileTypeStep.inscription')}
          </div>
          <div className="font-ambit text-[1.0625rem] not-italic font-normal leading-[1.5625rem] text-[#3B5266]">
            {t('profileTypeStep.as')}
          </div>
          <button
            className="w-full bg-primary text-white py-2 px-4 rounded-full font-ambit text-[1.0625rem] not-italic font-[900] leading-[1.5625rem] mt-11"
            onClick={() => handleProfileType(ROLES.FREELANCER)}
          >
            {t('common:role.freelancer')}
          </button>
          <button
            className="w-full bg-primary text-white py-2 px-4 rounded-full font-ambit text-[1.0625rem] not-italic font-[900] leading-[1.5625rem] mt-4"
            onClick={() => handleProfileType(ROLES.RECRUITER)}
          >
            {t('common:role.recruiter')}
          </button>
        </div>
      </div>
    </div>
  );
}
